<template>
  <template v-if="inline">
    <PrmSkeleton
      v-if="isLoading"
      style="width: 30%; height: 1.75rem;"
    />
    <template
      v-for="item in flatAppMenu"
      v-else
      :key="item.app?.appId"
    >
      <RouterLinkApp
        v-if="item.app"
        :app-id="item.app.appId"
        :route-to="item.routeTo!"
      />
    </template>
  </template>
  <template v-else>
    <slot
      name="button"
      :loading="isLoading"
      :toggle-menu="toggleMenu"
    >
      <PrmButton
        type="button"
        severity="secondary"
        :loading="isLoading"
        text
        icon="pi pi-arrow-right-arrow-left"
        size="small"
        data-qa="asset-quick-navigation__toggle"
        @click="toggleMenu"
      />
    </slot>
    <PrmMenu
      ref="assetQuickNavigation"
      :model="menuItems"
      popup
      data-qa="asset-quick-navigation"
    >
      <template #start>
        <div class="menu-item menu-item__headline">
          {{ i18n.global.t(`${I18N_PREFIX}.header`, {
            assetDisplayName: assetStore.assetOverview(props.assetId)?.displayName,
          }) }}
        </div>
      </template>
      <template
        #submenuheader="{
          item,
        }: {item: TAssetQuickNavigationMenu}"
      >
        <div class="menu-item">
          {{ item.label }}
        </div>
      </template>
      <template
        #item="{
          item, props: slotProps,
        }: {item: TAssetQuickNavigationMenu, props: MenuRouterBindProps}"
      >
        <router-link
          v-if="item.routeTo && item.app"
          v-slot="{
            href, navigate,
          }"
          :to="item.routeTo"
          custom
        >
          <a
            :href="href"
            v-bind="slotProps.action"
            class="menu-item menu-item__app"
            :style="`border-color: ${appTileColorMap[appStore.getAppGroup(item.app)]};`"
            @click="navigate"
          >
            <component
              :is="appListMap[item.app.appId].icon"
              class="menu-item__icon"
            />
            <span class="menu-item__label">{{ item.label }}</span>
          </a>
        </router-link>
      </template>
    </PrmMenu>
  </template>
</template>

<script setup lang="ts">
import PrmButton from '@NitrexGroup/primevue/button';
import PrmMenu, {

  MenuRouterBindProps,
} from '@NitrexGroup/primevue/menu';
import {
  MenuItem,
} from '@NitrexGroup/primevue/menuitem';
import PrmSkeleton from '@NitrexGroup/primevue/skeleton';
import {
  i18n,
} from '@i18n';
import {
  computed,
  shallowRef,
} from 'vue';
import {
  RouteLocationRaw,
} from 'vue-router';

import {
  LAST_JOB_RANGE_QUERY,
} from '@/components/date-range-picker/date-range-picker.ts';
import RouterLinkApp from '@/components/router-link-app/router-link-app.vue';
import {
  ERouteName,
} from '@/router/route-name.ts';
import {
  useAppStore,
} from '@/store/pinia/app.ts';
import {
  useAssetStore,
} from '@/store/pinia/asset.ts';
import {
  EAppId, TApp, TAppMap,
} from '@/types/app-type.ts';
import {
  appListMap,
  getAppMap,
} from '@/types/app-util.ts';
import {
  EHierarchy,
} from '@/types/asset-tree.ts';
import {
  appTileColorMap,
} from '@/utils/app-tile.ts';

const I18N_PREFIX = 'components/menu-asset-app-navigation/menu-asset-app-navigation';

const assetStore = useAssetStore();
assetStore.loadAssetOverview();
const appStore = useAppStore();
appStore.loadAppList();
appStore.loadCurrentUser();

const props = defineProps<{
  assetId: number,
  /**
   * If set to true, this component will no longer show one button that opens a menu
   * but rather provides a button for each app individually. You have control on how
   * these buttons are displayed (row/column, gap), by controlling the container
   * containing this component.
   */
  inline?: boolean,
  /** When true, the LIVE app link will include range=byJob::lastJob in the query. */
  includeLiveJobRange?: boolean,
}>();

defineSlots<{
  button(props: {
    loading: boolean,
    toggleMenu: (event: Event) => void,
  }): any,
}>();

interface TAssetQuickNavigationMenu extends Omit<MenuItem, 'icon' | 'items'> {
  routeTo?: RouteLocationRaw,
  items?: TAssetQuickNavigationMenu[],
  app?: TApp,
}
const isLoading = computed<boolean>(() => appStore.appListIsLoading || appStore.isUserLoading || assetStore.isAssetOverviewLoading);
const assetQuickNavigation = shallowRef<PrmMenu>();
const menuItems = computed<TAssetQuickNavigationMenu[]>(() => {
  const appMap: TAppMap = appStore.appList
    ? getAppMap(appStore.appList)
    : {} as TAppMap;
  const menu: TAssetQuickNavigationMenu[] = [];

  Object.entries(appMap).forEach(([
    appGroup,
    appList,
  ]) => {
    const groupMenu: TAssetQuickNavigationMenu = {
      label: appGroup,
      items: [],
    };

    appList.forEach((app) => {
      const assetDetails = assetStore.assetOverview(props.assetId);
      if (!assetDetails) {
        return;
      }

      const appMenuEntry: TAssetQuickNavigationMenu = {
        label: app.displayName,
        disabled: !appStore.user?.appPermissions?.[app.appId],
        app,
      };

      switch (app.appId) {
        case EAppId.ALARM_STATISTICS:
          if (assetDetails.hierarchyLevel === EHierarchy.EQUIPMENT) {
            appMenuEntry.routeTo = {
              name: ERouteName.ALARM_STATISTICS_EQUIPMENT,
              params: {
                assetId: props.assetId,
              },
            };
          }
          break;

        case EAppId.ASSET_MANAGEMENT:
          appMenuEntry.routeTo = {
            name: assetDetails.hierarchyLevel === EHierarchy.EQUIPMENT ? ERouteName.ASSET_MANAGEMENT_MAIN_EQUIPMENT : ERouteName.ASSET_MANAGEMENT_MAIN,
            params: {
              assetId: props.assetId,
              assetHierarchyLevel: assetDetails.hierarchyLevel,
            },
          };
          break;

        case EAppId.LIVE:
          appMenuEntry.routeTo = {
            name: assetDetails.hierarchyLevel === EHierarchy.EQUIPMENT ? ERouteName.LIVE_APP_DETAILS : ERouteName.LIVE_APP_OVERVIEW,
            params: {
              assetId: props.assetId,
            },
            ...(props.includeLiveJobRange
              ? {
                query: {
                  range: LAST_JOB_RANGE_QUERY,
                },
              }
              : {}),
          };
          break;

        case EAppId.HISTORY_AND_TRENDS:
          if (assetDetails.hierarchyLevel === EHierarchy.EQUIPMENT) {
            appMenuEntry.routeTo = {
              name: ERouteName.HISTORY_AND_TRENDS,
              params: {
                assetId: props.assetId,
              },
            };
          }
          break;

        case EAppId.REMOTE_CONTROL:
          if (assetDetails.hierarchyLevel === EHierarchy.EQUIPMENT) {
            appMenuEntry.routeTo = {
              name: ERouteName.REMOTE_CONTROL_DETAILS,
              params: {
                assetId: props.assetId,
              },
            };
          }
          break;

        case EAppId.PERFORMANCE_DASHBOARD:
          appMenuEntry.routeTo = {
            name: ERouteName.PERFORMANCE_DASHBOARD_DETAILS,
            params: {
              assetId: props.assetId,
            },
          };
          break;

        case EAppId.PRODUCTION_LOGS:
          appMenuEntry.routeTo = {
            name: ERouteName.PRODUCTION_LOGS,
            query: {
              assetIds: [
                props.assetId,
              ],
            },
          };
          break;

        case EAppId.PRODUCTION_STATISTICS:
          appMenuEntry.routeTo = {
            name: ERouteName.PRODUCTION_STATISTICS_DETAILS,
            params: {
              assetId: props.assetId,
              statisticsLevel: assetDetails.hierarchyLevel,
            },
          };
          break;

        case EAppId.ANOMALY_DETECTION:
          if (assetDetails.hierarchyLevel === EHierarchy.EQUIPMENT) {
            appMenuEntry.routeTo = {
              name: ERouteName.ANOMALY_DETECTION_EQUIPMENT,
              params: {
                assetId: props.assetId,
              },
            };
          }
          break;

        case EAppId.RECIPE:
          if (assetDetails.hierarchyLevel === EHierarchy.AUTOMAT || assetDetails.hierarchyLevel === EHierarchy.EQUIPMENT) {
            appMenuEntry.routeTo = {
              name: ERouteName.RECIPE_BUILDER_ASSET,
              params: {
                assetId: props.assetId,
              },
            };
          }
          break;

        case EAppId.QMULUS_DASHBOARD:
          appMenuEntry.routeTo = {
            name: ERouteName.QMULUS_DASHBOARD_ASSET,
            params: {
              assetId: props.assetId,
            },
          };
          break;
      }

      if (appMenuEntry.routeTo) {
        groupMenu.items!.push(appMenuEntry);
      }
    });

    if (groupMenu.items?.length) {
      menu.push(groupMenu);
    }
  });

  return menu;
});

function flattenMenuItems<T extends MenuItem>(menu: T[]): T[] {
  const flattenedMenu: T[] = [];

  menu.forEach((item) => {
    if (item.items?.length) {
      const subItems = flattenMenuItems(item.items as T[]);
      if (subItems.length) {
        flattenedMenu.push(...subItems);
      }
    }
    // we are only interested in menu items that are a leaf but not a headeline.
    if (item.app) {
      flattenedMenu.push(item);
    }
  });

  return flattenedMenu;
}

const flatAppMenu = computed<TAssetQuickNavigationMenu[]>(() => flattenMenuItems(menuItems.value).filter((item) => !!item.app));

function toggleMenu(event: Event) {
  assetQuickNavigation.value?.toggle(event);
}
</script>

<style scoped lang="scss">
.menu-item {
  font-size: 0.875rem;

  &__app {
    border-left: 0.5rem solid;
  }

  &__headline {
    padding: 0.75rem 1.25rem;
    font-weight: normal;
    font-size: 1.125rem;
  }

  &__icon {
    width: 1.5rem;
    height: 1.5rem;
  }

  &__label {
    margin-left: var(--gap);
  }
}
</style>
